/**
 * Blackjack — 3-deck shoe, up to 3 simultaneous hands (one player).
 * Pure logic; GBUX debit/credit lives in the Worker.
 */

export type Suit = "hearts" | "diamonds" | "clubs" | "spades";
export type Rank =
  | "A"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "J"
  | "Q"
  | "K"
  | "?";

export interface Card {
  suit: Suit;
  rank: Rank;
  value: number;
}

export const BJ_DECKS = 3;
export const BJ_MAX_HANDS = 3;
export const BJ_MIN_BET = 1;
/** Max *initial* wager per hand slot (doubles/splits may raise total above this). */
export const BJ_MAX_INITIAL_BET = 500;
/** @deprecated use BJ_MAX_INITIAL_BET — kept for imports */
export const BJ_MAX_BET = BJ_MAX_INITIAL_BET;
/** Blackjack pays 3:2 */
export const BJ_NATURAL_MULT = 1.5;
/** Reshuffle when remaining cards below this */
export const BJ_RESHUFFLE_AT = 40;

/**
 * Product math disclosure (player vs BUDBai / agent house bank).
 * Wins pay from agent play ledger; bets credit agent — real D1 GBUX only.
 */
export const BJ_MATH = {
  decks: BJ_DECKS,
  maxHands: BJ_MAX_HANDS,
  minBet: BJ_MIN_BET,
  maxInitialBet: BJ_MAX_INITIAL_BET,
  naturalPayout: "3:2",
  naturalMult: BJ_NATURAL_MULT,
  evenMoney: "1:1",
  dealerHitsSoft17: false,
  dealerStandsSoft17: true,
  doubleOn: "first two cards only",
  shoe: "3×52 · crypto shuffle · reshuffle under 40 remaining",
  house: "BUDBai agent play ledger (same wallet as rake / sales agent)",
  currency: "real play GBUX (D1 dApp ledger — not fake chips)",
  rateUsd: 0.001,
} as const;

/** Chip denominations for tray (match /public/chips/*.png) */
export const BJ_CHIP_DENOMS = [1, 5, 10, 25, 50, 100, 500, 1000] as const;

export type HandStatus =
  | "idle"
  | "betting"
  | "active"
  | "stood"
  | "bust"
  | "blackjack"
  | "doubled"
  | "settled";

export type RoundPhase = "idle" | "player" | "dealer" | "settled";

export interface BjHand {
  index: number;
  bet: number;
  cards: Card[];
  status: HandStatus;
  /** Net GBUX delta after settle (positive = win) */
  result?: number;
  /** True when this hand took a double */
  doubled?: boolean;
}

export interface BjPublicState {
  phase: RoundPhase;
  hands: BjHand[];
  dealer: { cards: Card[]; holeHidden: boolean };
  shoeRemaining: number;
  message?: string;
  playGbux?: number;
}

const SUITS: Suit[] = ["hearts", "diamonds", "clubs", "spades"];
const RANKS: Rank[] = [
  "A",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
];

function rankValuePoker(rank: Rank): number {
  if (rank === "A") return 14;
  if (rank === "K") return 13;
  if (rank === "Q") return 12;
  if (rank === "J") return 11;
  return parseInt(rank, 10);
}

function makeDeck(): Card[] {
  const d: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      d.push({ suit, rank, value: rankValuePoker(rank) });
    }
  }
  return d;
}

export function makeShoe(decks = BJ_DECKS): Card[] {
  const shoe: Card[] = [];
  for (let i = 0; i < decks; i++) shoe.push(...makeDeck());
  return shoe;
}

function secureIndex(max: number): number {
  if (max <= 1) return 0;
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const buf = new Uint32Array(1);
  let x = 0;
  do {
    crypto.getRandomValues(buf);
    x = buf[0]!;
  } while (x >= limit);
  return x % max;
}

export function shuffleShoe(shoe: Card[]): Card[] {
  const a = shoe.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = secureIndex(i + 1);
    const t = a[i]!;
    a[i] = a[j]!;
    a[j] = t;
  }
  return a;
}

/** Blackjack soft/hard total */
export function handTotal(cards: Card[]): { total: number; soft: boolean } {
  let total = 0;
  let aces = 0;
  for (const c of cards) {
    if (c.rank === "A") {
      aces++;
      total += 11;
    } else if (["K", "Q", "J", "10"].includes(c.rank)) {
      total += 10;
    } else {
      total += parseInt(c.rank, 10);
    }
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces--;
  }
  const soft = aces > 0 && total <= 21;
  return { total, soft };
}

export function isBlackjack(cards: Card[]): boolean {
  return cards.length === 2 && handTotal(cards).total === 21;
}

export function isBust(cards: Card[]): boolean {
  return handTotal(cards).total > 21;
}

export interface BjSessionInternal {
  shoe: Card[];
  hands: BjHand[];
  dealer: Card[];
  phase: RoundPhase;
  message: string;
}

export function emptySession(): BjSessionInternal {
  return {
    shoe: shuffleShoe(makeShoe()),
    hands: [0, 1, 2].map((i) => ({
      index: i,
      bet: 0,
      cards: [],
      status: "idle" as HandStatus,
    })),
    dealer: [],
    phase: "idle",
    message: "Place bets on 1–3 hands",
  };
}

function draw(s: BjSessionInternal): Card {
  if (s.shoe.length < BJ_RESHUFFLE_AT) {
    s.shoe = shuffleShoe(makeShoe());
  }
  const c = s.shoe.pop();
  if (!c) {
    s.shoe = shuffleShoe(makeShoe());
    return s.shoe.pop()!;
  }
  return c;
}

export function toPublic(
  s: BjSessionInternal,
  revealDealer: boolean,
): BjPublicState {
  const holeHidden = s.phase === "player" && !revealDealer && s.dealer.length >= 2;
  const dealerCards = holeHidden
    ? [
        s.dealer[0]!,
        { suit: "spades" as Suit, rank: "?" as Rank, value: 0 },
      ]
    : s.dealer;
  return {
    phase: s.phase,
    hands: s.hands.map((h) => ({ ...h, cards: h.cards.map((c) => ({ ...c })) })),
    dealer: {
      cards: dealerCards.map((c) => ({ ...c })),
      holeHidden,
    },
    shoeRemaining: s.shoe.length,
    message: s.message,
  };
}

/**
 * Start a round. `bets` length 3; 0 = hand not playing.
 * Returns total wager for debit.
 */
export function dealRound(
  s: BjSessionInternal,
  bets: number[],
): { ok: true; wager: number } | { ok: false; error: string } {
  if (s.phase === "player" || s.phase === "dealer") {
    return { ok: false, error: "Round already in progress" };
  }
  if (!Array.isArray(bets) || bets.length !== BJ_MAX_HANDS) {
    return { ok: false, error: "Provide 3 bet amounts (0 to skip a hand)" };
  }
  let wager = 0;
  const hands: BjHand[] = [];
  for (let i = 0; i < BJ_MAX_HANDS; i++) {
    const b = Math.floor(Number(bets[i]) || 0);
    if (b < 0) return { ok: false, error: "Invalid bet" };
    if (b > 0 && (b < BJ_MIN_BET || b > BJ_MAX_INITIAL_BET)) {
      return {
        ok: false,
        error: `Initial bet must be ${BJ_MIN_BET}–${BJ_MAX_INITIAL_BET} GBUX per hand (doubles may exceed)`,
      };
    }
    wager += b;
    hands.push({
      index: i,
      bet: b,
      cards: [],
      status: b > 0 ? "active" : "idle",
    });
  }
  if (wager < BJ_MIN_BET) {
    return { ok: false, error: "Bet at least one hand" };
  }

  s.hands = hands;
  s.dealer = [];
  s.message = "";

  // Deal two rounds: player hands then dealer, then player, then dealer
  for (let r = 0; r < 2; r++) {
    for (const h of s.hands) {
      if (h.bet > 0) h.cards.push(draw(s));
    }
    s.dealer.push(draw(s));
  }

  // Naturals
  let anyPlay = false;
  for (const h of s.hands) {
    if (h.bet <= 0) continue;
    if (isBlackjack(h.cards)) {
      h.status = "blackjack";
    } else {
      h.status = "active";
      anyPlay = true;
    }
  }

  const dealerBj = isBlackjack(s.dealer);
  if (dealerBj) {
    // Immediate settle all
    s.phase = "settled";
    settleAll(s, true);
    s.message = "Dealer blackjack";
    return { ok: true, wager };
  }

  if (!anyPlay) {
    // All player naturals — settle vs dealer non-BJ
    s.phase = "settled";
    settleAll(s, false);
    s.message = "Blackjack!";
    return { ok: true, wager };
  }

  s.phase = "player";
  s.message = "Your turn — hit, stand, or double";
  return { ok: true, wager };
}

function activeHands(s: BjSessionInternal): BjHand[] {
  return s.hands.filter((h) => h.status === "active" || h.status === "doubled");
}

function allPlayerDone(s: BjSessionInternal): boolean {
  return s.hands.every(
    (h) =>
      h.bet <= 0 ||
      h.status === "stood" ||
      h.status === "bust" ||
      h.status === "blackjack" ||
      h.status === "settled" ||
      h.status === "idle",
  );
}

export function hit(
  s: BjSessionInternal,
  handIndex: number,
): { ok: true } | { ok: false; error: string } {
  if (s.phase !== "player") return { ok: false, error: "Not player phase" };
  const h = s.hands[handIndex];
  if (!h || h.bet <= 0) return { ok: false, error: "Invalid hand" };
  if (h.status !== "active" && h.status !== "doubled") {
    return { ok: false, error: "Hand not active" };
  }
  h.cards.push(draw(s));
  if (isBust(h.cards)) {
    h.status = "bust";
    h.result = -h.bet;
  } else if (h.status === "doubled") {
    h.status = "stood";
  }
  if (allPlayerDone(s)) finishDealer(s);
  else s.message = `Hand ${handIndex + 1}`;
  return { ok: true };
}

export function stand(
  s: BjSessionInternal,
  handIndex: number,
): { ok: true } | { ok: false; error: string } {
  if (s.phase !== "player") return { ok: false, error: "Not player phase" };
  const h = s.hands[handIndex];
  if (!h || h.bet <= 0) return { ok: false, error: "Invalid hand" };
  if (h.status !== "active" && h.status !== "doubled") {
    return { ok: false, error: "Hand not active" };
  }
  h.status = "stood";
  if (allPlayerDone(s)) finishDealer(s);
  return { ok: true };
}

/** Double bet + one card then stand. Extra bet must be debited by caller. */
export function doubleDown(
  s: BjSessionInternal,
  handIndex: number,
): { ok: true; extraBet: number } | { ok: false; error: string } {
  if (s.phase !== "player") return { ok: false, error: "Not player phase" };
  const h = s.hands[handIndex];
  if (!h || h.bet <= 0) return { ok: false, error: "Invalid hand" };
  if (h.status !== "active") return { ok: false, error: "Cannot double" };
  if (h.cards.length !== 2) return { ok: false, error: "Double only on first two cards" };
  const extra = h.bet;
  h.bet *= 2;
  h.doubled = true;
  h.status = "doubled";
  h.cards.push(draw(s));
  if (isBust(h.cards)) {
    h.status = "bust";
    h.result = -h.bet;
  } else {
    h.status = "stood";
  }
  if (allPlayerDone(s)) finishDealer(s);
  return { ok: true, extraBet: extra };
}

function finishDealer(s: BjSessionInternal): void {
  // If all hands bust/BJ already settled partially
  const needDealer = s.hands.some(
    (h) => h.bet > 0 && (h.status === "stood" || h.status === "blackjack"),
  );
  s.phase = "dealer";
  if (needDealer) {
    // Dealer hits until hard 17+ (S17: stand soft 17)
    while (true) {
      const { total, soft } = handTotal(s.dealer);
      if (total > 21) break;
      if (total > 17) break;
      if (total === 17 && !soft) break;
      if (total === 17 && soft) break; // S17
      if (total < 17) {
        s.dealer.push(draw(s));
        continue;
      }
      break;
    }
  }
  settleAll(s, isBlackjack(s.dealer) && s.dealer.length === 2);
  s.phase = "settled";
  s.message = "Round complete";
}

function settleAll(s: BjSessionInternal, dealerNatural: boolean): void {
  const dTot = handTotal(s.dealer).total;
  const dBust = dTot > 21;
  for (const h of s.hands) {
    if (h.bet <= 0 || h.status === "idle") continue;
    if (h.status === "bust") {
      h.result = -h.bet;
      h.status = "settled";
      continue;
    }
    if (h.status === "blackjack") {
      if (dealerNatural) {
        h.result = 0; // push
      } else {
        h.result = Math.floor(h.bet * BJ_NATURAL_MULT); // net profit (3:2), stake returned separately
        // Net credit = bet + 1.5*bet = 2.5*bet; we track net P/L as +1.5*bet and return stake in credit path
      }
      h.status = "settled";
      continue;
    }
    // stood / doubled
    const pTot = handTotal(h.cards).total;
    if (dealerNatural) {
      h.result = -h.bet;
    } else if (dBust) {
      h.result = h.bet; // win 1:1 profit
    } else if (pTot > dTot) {
      h.result = h.bet;
    } else if (pTot < dTot) {
      h.result = -h.bet;
    } else {
      h.result = 0; // push
    }
    h.status = "settled";
  }
}

/** GBUX to credit after settle: stake returns + profit for wins; 0 for losses. */
export function creditAfterSettle(h: BjHand): number {
  if (h.bet <= 0) return 0;
  const r = h.result ?? 0;
  if (r > 0) {
    // profit r + return stake
    return h.bet + r;
  }
  if (r === 0 && h.status === "settled") {
    // push — return stake
    return h.bet;
  }
  // blackjack push already 0 result with stake return
  if (r === 0 && h.cards.length === 2 && isBlackjack(h.cards)) {
    return h.bet;
  }
  return 0; // loss — already debited
}

export function totalCreditForRound(s: BjSessionInternal): number {
  return s.hands.reduce((sum, h) => sum + creditAfterSettle(h), 0);
}

export function serializeSession(s: BjSessionInternal): string {
  return JSON.stringify(s);
}

export function parseSession(raw: string | null | undefined): BjSessionInternal {
  if (!raw) return emptySession();
  try {
    const j = JSON.parse(raw) as BjSessionInternal;
    if (!j.shoe || !j.hands) return emptySession();
    return j;
  } catch {
    return emptySession();
  }
}

/** Map suit to craftpix folder 1–4 */
export function craftpixSuitFolder(suit: Suit | string): string {
  switch (suit) {
    case "hearts":
      return "1";
    case "diamonds":
      return "2";
    case "clubs":
      return "3";
    case "spades":
      return "4";
    default:
      return "4";
  }
}

export function craftpixCardUrl(card: Card): string {
  if (card.rank === "?" || (card as { rank: string }).rank === "?") {
    // Face-down hole — production monogram back
    return "/media/card-back-production.jpg";
  }
  const folder = craftpixSuitFolder(card.suit);
  const file = card.rank === "10" ? "10" : card.rank;
  return `/blackjack/cards/${folder}/${file}.png`;
}
